import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Briefcase, MapPin, Clock, IndianRupee, Search, ArrowLeft, ArrowRight, Users, CheckCircle2, Award, Upload, Sparkles, HandHeart, TrendingUp, ShieldCheck, UserSearch, GraduationCap, FileText, Building2, ClipboardCheck, X } from "lucide-react";
import { VerticalHeader, VerticalFooter, VERTICAL_THEMES } from "./BrandChrome";
import "./asg.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated Unsplash imagery
const IMG_HERO = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80";
const IMG_SALES = "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80";
const IMG_OPS = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80";
const IMG_MARKETING = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80";
const IMG_DEFAULT = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80";

const IMG_BY_CATEGORY = {
  Sales: IMG_SALES,
  Operations: IMG_OPS,
  Marketing: IMG_MARKETING,
  Technology: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=800&q=80",
  Support: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?auto=format&fit=crop&w=800&q=80",
};

export function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/jobs`).then(r => setJobs(r.data || [])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(jobs.map(j => j.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (cat !== "all" && j.category !== cat) return false;
      if (q) {
        const s = q.toLowerCase();
        return (j.title || "").toLowerCase().includes(s)
          || (j.short_description || "").toLowerCase().includes(s)
          || (j.location || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [jobs, q, cat]);

  return (
    <div className="asg-shell asg-vertical-jobs" data-testid="jobs-page">
      <Helmet>
        <title>Careers at Arth Setuu Global — Jobs in Patna & Remote</title>
        <meta name="description" content="Explore open roles at Arth Setuu Global across Sales, Operations, Marketing, Support and Technology. Join our Patna-based team building yatra, jobs and loan experiences for India." />
        <link rel="canonical" href="https://arthsetuuglobal.in/jobs" />
      </Helmet>

      <VerticalHeader theme={VERTICAL_THEMES.jobs} currentVertical="jobs" />

      <section className="asg-vhero asg-vhero-image" style={{ backgroundImage: `url(${IMG_HERO})` }}>
        <div className="asg-vhero-inner">
          <span className="asg-vhero-eyebrow"><Briefcase size={12} /> Arth Setuu Global · HR Consultancy</span>
          <h1>Building careers.<br/>Connecting talent.</h1>
          <p>Professional recruitment and career solutions connecting the right talent with the right opportunities. With 21+ years of experience, we support both employers seeking quality candidates and professionals navigating their next career move.</p>
          <div className="asg-vhero-stats">
            <div><b>21+</b><span>Years in HR</span></div>
            <div><b>1,500+</b><span>Candidates placed</span></div>
            <div><b>{jobs.length}</b><span>Open roles today</span></div>
            <div><b>Patna + Remote</b><span>Work modes</span></div>
          </div>
        </div>
      </section>

      <div className="asg-container">
        <AboutSection />
        <ServicesSection />
        <ResumeServiceSection />
      </div>

      {/* Resume submission / quick apply on listing page */}
      <section className="asg-lead-band" data-testid="job-quick-apply-band">
        <div className="asg-lead-inner">
          <div className="asg-lead-copy">
            <span className="asg-hero-eyebrow" style={{ background: "#DBEAFE", color: "#1E3A8A", borderColor: "transparent" }}>Not sure which role?</span>
            <h2>Submit your resume — we'll find the right fit.</h2>
            <p>Don't see a role that matches? Send us your resume with a quick note about what you're looking for. We're always meeting bright people for future openings across Patna and remote.</p>
            <ul className="asg-lead-benefits">
              <li><ShieldCheck size={16} /> Resume kept confidential</li>
              <li><Clock size={16} /> Response within 3–5 days</li>
              <li><HandHeart size={16} /> Human review, no bots</li>
              <li><TrendingUp size={16} /> Considered across all future openings</li>
            </ul>
          </div>
          <QuickApplyForm jobs={jobs} />
        </div>
      </section>

      <div className="asg-container">
        <div className="asg-filters" data-testid="jobs-filters">
          <div className="asg-search-input">
            <Search size={16} color="#64748B" />
            <input
              placeholder="Search roles, skills, locations…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="jobs-search-input"
            />
          </div>
          {categories.map(c => (
            <button
              key={c}
              className={`asg-chip ${cat === c ? "active" : ""}`}
              onClick={() => setCat(c)}
              data-testid={`jobs-chip-${c}`}
            >
              {c === "all" ? "All roles" : c}
            </button>
          ))}
        </div>

        <div className="asg-section-title">
          <div>
            <h2>Open positions</h2>
            <p>{filtered.length} role{filtered.length === 1 ? "" : "s"} matching your search.</p>
          </div>
        </div>

        {loading ? (
          <div className="asg-empty">Loading roles…</div>
        ) : filtered.length === 0 ? (
          <div className="asg-empty" data-testid="jobs-empty">
            <b>No roles match this search</b>
            Try a different keyword or clear the filters — new roles open often.
          </div>
        ) : (
          <div className="asg-grid" data-testid="jobs-grid">
            {filtered.map(j => (
              <div key={j.slug} className="asg-card asg-job-card" data-testid={`job-card-${j.slug}`}>
                <div className="asg-card-image" style={{ backgroundImage: `url(${IMG_BY_CATEGORY[j.category] || IMG_DEFAULT})` }}>
                  <span className="asg-card-tag" style={{ position: "absolute", left: 16, top: 16 }}>{j.category}</span>
                </div>
                <div className="asg-job-card-body">
                  <h3>{j.title}</h3>
                  <p>{j.short_description}</p>
                  <div className="asg-card-meta">
                    <span><MapPin size={13} /> {j.location}</span>
                    <span><Clock size={13} /> {j.employment_type}</span>
                    <span><Users size={13} /> {j.experience}</span>
                  </div>
                  <div className="asg-card-meta" style={{ marginBottom: 20 }}>
                    <span style={{ color: "#1D4ED8", fontWeight: 700 }}><IndianRupee size={13} /> {j.salary_range}</span>
                  </div>
                  <Link to={`/jobs/${j.slug}`} className="asg-card-link" data-testid={`job-view-${j.slug}`}>
                    View role & apply <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <CultureSection />
      </div>

      <VerticalFooter theme={VERTICAL_THEMES.jobs} />
    </div>
  );
}

function CultureSection() {
  const items = [
    { icon: <HandHeart size={22} />, t: "People-first culture", d: "We hire kind, curious people and trust them with real ownership from day one." },
    { icon: <TrendingUp size={22} />, t: "Growth path", d: "Clear career tracks, performance bonuses and internal moves across Yatra / Jobs / Loans verticals." },
    { icon: <Sparkles size={22} />, t: "Meaningful work", d: "Every role directly touches pilgrims, families and small businesses across India." },
    { icon: <Award size={22} />, t: "Recognition & rewards", d: "Monthly star awards, referral bonuses and travel exposure across our yatra portfolio." },
  ];
  return (
    <section className="asg-trust" data-testid="jobs-culture">
      <div className="asg-section-title">
        <div>
          <h2>Why join Arth Setuu Global?</h2>
          <p>We're building a rare workplace — mission-driven, warmly human, and unafraid to move fast.</p>
        </div>
      </div>
      <div className="asg-trust-grid">
        {items.map((it, i) => (
          <div key={i} className="asg-trust-card">
            <span className="asg-trust-icon">{it.icon}</span>
            <b>{it.t}</b>
            <p>{it.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function QuickApplyForm({ jobs }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "",
    role_interest: "", experience_years: "", cover_note: "", consent: false
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    if (!form.name || !form.mobile) { setErr("Name and mobile are required."); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    if (file) fd.append("resume", file);
    setBusy(true);
    try {
      await axios.post(`${API}/jobs-enquiry`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (ok) return (
    <div className="asg-card asg-lead-form-card" data-testid="quick-apply-success">
      <div className="asg-form-success">
        <div className="asg-check"><CheckCircle2 size={30} /></div>
        <h4>Application received</h4>
        <p>Thanks {form.name.split(" ")[0]} — we've got your resume{form.role_interest ? <> for <b>{form.role_interest}</b></> : null}. Our team will review and get back within 3–5 working days.</p>
      </div>
    </div>
  );

  return (
    <div className="asg-card asg-lead-form-card">
      <h3 className="asg-form-title">Submit your resume</h3>
      <p className="asg-form-sub">One-minute form. Attach your resume and we'll take it from there.</p>
      <form onSubmit={submit} data-testid="quick-apply-form">
        {err && <div className="asg-form-error" data-testid="quick-apply-error">{err}</div>}
        <div className="asg-form-row">
          <div className="asg-form-group">
            <label>Full name *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="quick-input-name" />
          </div>
          <div className="asg-form-group">
            <label>Mobile *</label>
            <input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="quick-input-mobile" />
          </div>
        </div>
        <div className="asg-form-row">
          <div className="asg-form-group">
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="quick-input-email" />
          </div>
          <div className="asg-form-group">
            <label>City</label>
            <input value={form.city} onChange={e => set("city", e.target.value)} data-testid="quick-input-city" />
          </div>
        </div>
        <div className="asg-form-group">
          <label>Role you're interested in</label>
          <select value={form.role_interest} onChange={e => set("role_interest", e.target.value)} data-testid="quick-input-role">
            <option value="">Any role / Not sure</option>
            {jobs.map(j => <option key={j.slug} value={j.title}>{j.title}</option>)}
            <option value="Other">Other (mention in note)</option>
          </select>
        </div>
        <div className="asg-form-group">
          <label>Total experience</label>
          <input placeholder="e.g. Fresher, 2 years, 5+ years" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} data-testid="quick-input-exp" />
        </div>
        <div className="asg-form-group">
          <label>Cover note (optional)</label>
          <textarea placeholder="Anything we should know?" value={form.cover_note} onChange={e => set("cover_note", e.target.value)} data-testid="quick-input-note" />
        </div>
        <div className="asg-form-group">
          <label><Upload size={11} style={{marginRight:4,marginBottom:-1}} />Resume (PDF, DOC, DOCX, JPG, JPEG, PNG — up to 5MB)</label>
          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0] || null)} className="asg-file-input" data-testid="quick-input-resume" />
        </div>
        <label className="asg-form-consent">
          <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} data-testid="quick-input-consent" />
          <span>I consent to Arth Setuu Global contacting me via call/WhatsApp/email for this and future openings.</span>
        </label>
        <button className="asg-btn-primary" type="submit" disabled={busy} data-testid="quick-apply-submit">
          {busy ? "Submitting…" : <>Submit resume <ArrowRight size={14} /></>}
        </button>
        <p className="asg-form-note"><Award size={11} style={{marginRight:4,marginBottom:-1}} />Your data is used solely for hiring evaluation.</p>
      </form>
    </div>
  );
}

export function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs/${slug}`)
      .then(r => setJob(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="asg-shell asg-vertical-jobs">
        <VerticalHeader theme={VERTICAL_THEMES.jobs} currentVertical="jobs" />
        <div className="asg-container">
          <div className="asg-empty"><b>Role not found</b>This role may be closed. <Link to="/jobs">Explore all open roles</Link>.</div>
        </div>
        <VerticalFooter theme={VERTICAL_THEMES.jobs} />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="asg-shell asg-vertical-jobs">
        <VerticalHeader theme={VERTICAL_THEMES.jobs} currentVertical="jobs" />
        <div className="asg-container"><div className="asg-empty">Loading role…</div></div>
      </div>
    );
  }

  const heroImg = IMG_BY_CATEGORY[job.category] || IMG_DEFAULT;

  return (
    <div className="asg-shell asg-vertical-jobs" data-testid="job-detail-page">
      <Helmet>
        <title>{`${job.title} — Careers at Arth Setuu Global`}</title>
        <meta name="description" content={job.short_description} />
        <link rel="canonical" href={`https://arthsetuuglobal.in/jobs/${job.slug}`} />
      </Helmet>

      <VerticalHeader theme={VERTICAL_THEMES.jobs} currentVertical="jobs" />

      <section className="asg-vhero asg-detail-hero asg-vhero-image" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="asg-vhero-inner">
          <Link to="/jobs" className="asg-detail-hero-back"><ArrowLeft size={14} /> All roles</Link>
          <span className="asg-vhero-eyebrow">{job.category}</span>
          <h1>{job.title}</h1>
          <div className="asg-tags">
            <span><MapPin size={11} style={{marginRight:4,marginBottom:-1}} />{job.location}</span>
            <span><Clock size={11} style={{marginRight:4,marginBottom:-1}} />{job.employment_type}</span>
            <span><Users size={11} style={{marginRight:4,marginBottom:-1}} />{job.experience}</span>
            <span style={{ background: "#FDE68A", color: "#78350F" }}><IndianRupee size={11} style={{marginRight:4,marginBottom:-1}} />{job.salary_range}</span>
          </div>
        </div>
      </section>

      <div className="asg-container">
        <div className="asg-detail">
          <div className="asg-detail-main">
            <p style={{ fontSize: 17, color: "#0B1220", fontWeight: 500 }}>{job.short_description}</p>
            {job.full_description && (
              <>
                <h2>About the role</h2>
                <p>{job.full_description}</p>
              </>
            )}
            {job.responsibilities?.length > 0 && (
              <>
                <h2>Responsibilities</h2>
                <ul>{job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}
            {job.requirements?.length > 0 && (
              <>
                <h2>Requirements</h2>
                <ul>{job.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}
            {job.benefits?.length > 0 && (
              <>
                <h2>Benefits</h2>
                <ul>{job.benefits.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}
          </div>

          <aside className="asg-detail-side">
            <ApplicationForm job={job} />
          </aside>
        </div>
      </div>

      <VerticalFooter theme={VERTICAL_THEMES.jobs} />
    </div>
  );
}

function AboutSection() {
  return (
    <section className="asg-about" data-testid="jobs-about">
      <div className="asg-about-inner">
        <span className="asg-about-eyebrow">About the HR Consultancy</span>
        <h2>Experience that connects people and opportunities.</h2>
        <p>
          With over <b>21 years of experience</b> in recruitment, placement and talent acquisition, Arth Setuu Global's HR Consultancy division brings a deep understanding of employer requirements and candidate aspirations. We have supported the careers of <b>1,500+ candidates</b>, helping professionals identify suitable opportunities and helping organisations connect with relevant talent.
        </p>
        <div className="asg-about-metrics">
          <div><b>21+</b><span>Years in HR</span></div>
          <div><b>1,500+</b><span>Candidates placed</span></div>
          <div><b>Pan-India</b><span>Placement reach</span></div>
        </div>
        <p>Our approach combines industry understanding, candidate assessment, employer requirement analysis, career guidance, recruitment support and long-term relationship building.</p>
        <p className="asg-about-tagline">"The right candidate. The right opportunity. The right fit."</p>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    { icon: <UserSearch size={22} />, t: "Recruitment & Placement", d: "Connecting organisations with qualified candidates across departments and industries." },
    { icon: <Users size={22} />, t: "Talent Acquisition", d: "Helping businesses identify and hire the right talent for critical roles and growth phases." },
    { icon: <GraduationCap size={22} />, t: "Career Guidance", d: "Helping candidates make informed decisions about roles, industries and career transitions." },
    { icon: <FileText size={22} />, t: "Resume Services", d: "Professional resume creation and optimisation for modern recruitment processes." },
    { icon: <Briefcase size={22} />, t: "Job Opportunities", d: "Curated employment opportunities across relevant industries and experience levels." },
    { icon: <Building2 size={22} />, t: "Employer Support", d: "Assistance with candidate sourcing, screening and end-to-end recruitment requirements." },
  ];
  return (
    <section className="asg-services" data-testid="jobs-services">
      <div className="asg-section-title">
        <div>
          <h2>Our HR Consultancy Services</h2>
          <p>Six ways we support employers and professionals — each built on 21 years of hands-on recruitment work.</p>
        </div>
      </div>
      <div className="asg-services-grid">
        {services.map((s, i) => (
          <div key={i} className="asg-service-card" data-testid={`service-card-${i}`}>
            <span className="asg-service-icon">{s.icon}</span>
            <h4>{s.t}</h4>
            <p>{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ResumeServiceSection() {
  const [openModal, setOpenModal] = useState(false);
  return (
    <>
      <section className="asg-resume-band" data-testid="resume-service-band">
        <div className="asg-resume-inner">
          <div className="asg-resume-copy">
            <span className="asg-hero-eyebrow">Premium Career Service</span>
            <h2>Get your professional resume.</h2>
            <div className="asg-resume-price">₹499<small>one-time</small></div>
            <p>A professionally structured, modern resume + a one-time career guidance session + priority consideration for relevant opportunities handled by our Jobs division.</p>
            <ul className="asg-resume-includes">
              <li><CheckCircle2 size={16} /><span><b>Professional Resume Creation</b> — a high-quality resume designed for modern recruitment processes.</span></li>
              <li><CheckCircle2 size={16} /><span><b>One-Time Career Guidance</b> — a session focused on career direction, role suitability, positioning and application strategy.</span></li>
              <li><CheckCircle2 size={16} /><span><b>Application Preference</b> — eligible customers receive priority consideration for relevant opportunities.</span></li>
            </ul>
            <button className="asg-resume-cta" onClick={() => setOpenModal(true)} data-testid="resume-service-cta">
              GET STARTED — ₹499 <ArrowRight size={16} />
            </button>
            <p className="asg-resume-fineprint" style={{ marginTop: 14 }}>Subject to job availability and candidate suitability. No guaranteed job, interview, placement or salary.</p>
          </div>
          <div className="asg-card asg-lead-form-card" style={{ background: "#fff" }}>
            <h3 className="asg-form-title">Order at a glance</h3>
            <p className="asg-form-sub">Everything you'll receive after placing your order.</p>
            <ul className="asg-resume-includes" style={{ marginBottom: 8 }}>
              <li><ClipboardCheck size={16} /><span>Structured 1-2 page professional resume (PDF)</span></li>
              <li><ClipboardCheck size={16} /><span>ATS-friendly formatting for online applications</span></li>
              <li><ClipboardCheck size={16} /><span>30-min career guidance call (WhatsApp/Phone)</span></li>
              <li><ClipboardCheck size={16} /><span>Priority tag against your candidate profile</span></li>
              <li><ClipboardCheck size={16} /><span>Response within 3–5 working days</span></li>
            </ul>
            <button className="asg-btn-primary" onClick={() => setOpenModal(true)} data-testid="resume-service-start-btn">
              Get started — ₹499 <ArrowRight size={14} />
            </button>
            <p className="asg-form-note">V1: this is an order request. Payment collected offline (UPI / bank transfer) after our team confirms.</p>
          </div>
        </div>
      </section>
      {openModal && <ResumeServiceModal onClose={() => setOpenModal(false)} />}
    </>
  );
}

function ResumeServiceModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "",
    current_designation: "", experience_years: "",
    target_role: "", preferred_location: "",
    career_objective: "", consent: false
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    if (!form.name || !form.mobile) { setErr("Name and mobile are required."); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    if (file) fd.append("resume", file);
    setBusy(true);
    try {
      await axios.post(`${API}/resume-service`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="asg-modal-backdrop" onClick={onClose} data-testid="resume-service-modal">
      <div className="asg-modal" onClick={e => e.stopPropagation()}>
        <button className="asg-modal-close" onClick={onClose} aria-label="Close" data-testid="resume-service-close"><X size={20} /></button>
        {ok ? (
          <div className="asg-form-success" data-testid="resume-service-success">
            <div className="asg-check"><CheckCircle2 size={30} /></div>
            <h4>Order received</h4>
            <p>Thanks {form.name.split(" ")[0]} — your Professional Resume Service order is confirmed. Our team will call you within 24 hours to share payment details (₹499) and schedule your career guidance session.</p>
            <button className="asg-btn-primary" onClick={onClose} style={{ marginTop: 16, maxWidth: 200, marginInline: "auto" }}>Close</button>
          </div>
        ) : (
          <>
            <div className="asg-modal-head">
              <span className="asg-hero-eyebrow" style={{ background: "#FDE68A", color: "#78350F", borderColor: "transparent" }}>₹499 · Professional Resume Service</span>
              <h3>Get your professional resume</h3>
              <p>Fill in your details — our team will call within 24 hours with payment details and to schedule your guidance session.</p>
            </div>
            <form onSubmit={submit} data-testid="resume-service-form">
              {err && <div className="asg-form-error" data-testid="resume-service-error">{err}</div>}
              <div className="asg-form-row">
                <div className="asg-form-group">
                  <label>Full name *</label>
                  <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="rs-input-name" />
                </div>
                <div className="asg-form-group">
                  <label>Mobile *</label>
                  <input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="rs-input-mobile" />
                </div>
              </div>
              <div className="asg-form-row">
                <div className="asg-form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="rs-input-email" />
                </div>
                <div className="asg-form-group">
                  <label>Preferred location</label>
                  <input value={form.preferred_location} onChange={e => set("preferred_location", e.target.value)} data-testid="rs-input-location" />
                </div>
              </div>
              <div className="asg-form-row">
                <div className="asg-form-group">
                  <label>Current designation</label>
                  <input value={form.current_designation} onChange={e => set("current_designation", e.target.value)} placeholder="e.g. Sales Executive" data-testid="rs-input-designation" />
                </div>
                <div className="asg-form-group">
                  <label>Experience</label>
                  <input value={form.experience_years} onChange={e => set("experience_years", e.target.value)} placeholder="e.g. Fresher / 3 years" data-testid="rs-input-experience" />
                </div>
              </div>
              <div className="asg-form-group">
                <label>Target role</label>
                <input value={form.target_role} onChange={e => set("target_role", e.target.value)} placeholder="e.g. Marketing Manager" data-testid="rs-input-target-role" />
              </div>
              <div className="asg-form-group">
                <label>Career objective / message</label>
                <textarea value={form.career_objective} onChange={e => set("career_objective", e.target.value)} placeholder="Anything we should know about your career goals?" data-testid="rs-input-objective" />
              </div>
              <div className="asg-form-group">
                <label><Upload size={11} style={{marginRight:4,marginBottom:-1}} />Existing resume (optional — PDF, DOC, DOCX, JPG, JPEG, PNG)</label>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0] || null)} className="asg-file-input" data-testid="rs-input-resume" />
              </div>
              <label className="asg-form-consent">
                <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} data-testid="rs-input-consent" />
                <span>I consent to Arth Setuu Global contacting me via call/WhatsApp/email regarding this order.</span>
              </label>
              <button className="asg-btn-primary" type="submit" disabled={busy} data-testid="resume-service-submit">
                {busy ? "Submitting…" : <>Confirm order (₹499) <ArrowRight size={14} /></>}
              </button>
              <p className="asg-form-note">No online payment now — our team will share UPI / bank details on the confirmation call.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ApplicationForm({ job }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "",
    experience_years: "", cover_note: "", consent: false
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    if (!form.name || !form.mobile) { setErr("Name and mobile are required."); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    if (file) fd.append("resume", file);
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.slug}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (ok) return (
    <div className="asg-card">
      <div className="asg-form-success" data-testid="job-apply-success">
        <div className="asg-check"><CheckCircle2 size={30} /></div>
        <h4>Application received</h4>
        <p>Thanks {form.name.split(" ")[0]} — we've got your application for <b>{job.title}</b>. Our team will review and get back to you within 3–5 working days.</p>
      </div>
    </div>
  );

  return (
    <div className="asg-card">
      <h3 className="asg-form-title">Apply for this role</h3>
      <p className="asg-form-sub">Takes under 2 minutes. Attach your resume if you have one.</p>
      <form onSubmit={submit} data-testid="job-apply-form">
        {err && <div className="asg-form-error" data-testid="job-apply-error">{err}</div>}
        <div className="asg-form-group">
          <label>Full name *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="job-input-name" />
        </div>
        <div className="asg-form-group">
          <label>Mobile *</label>
          <input value={form.mobile} onChange={e => set("mobile", e.target.value)} required data-testid="job-input-mobile" />
        </div>
        <div className="asg-form-group">
          <label>Email</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="job-input-email" />
        </div>
        <div className="asg-form-group">
          <label>City</label>
          <input value={form.city} onChange={e => set("city", e.target.value)} data-testid="job-input-city" />
        </div>
        <div className="asg-form-group">
          <label>Total experience</label>
          <input placeholder="e.g. 2 years" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} data-testid="job-input-exp" />
        </div>
        <div className="asg-form-group">
          <label>Cover note</label>
          <textarea placeholder="Why this role?" value={form.cover_note} onChange={e => set("cover_note", e.target.value)} data-testid="job-input-note" />
        </div>
        <div className="asg-form-group">
          <label><Upload size={11} style={{marginRight:4,marginBottom:-1}} />Resume (PDF, DOC, DOCX, JPG, JPEG, PNG)</label>
          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0] || null)} className="asg-file-input" data-testid="job-input-resume" />
        </div>
        <label className="asg-form-consent">
          <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} data-testid="job-input-consent" />
          <span>I consent to Arth Setuu Global contacting me via call/WhatsApp/email regarding this application.</span>
        </label>
        <button className="asg-btn-primary" type="submit" disabled={busy} data-testid="job-apply-submit">
          {busy ? "Submitting…" : <>Submit application <ArrowRight size={14} /></>}
        </button>
        <p className="asg-form-note"><Award size={11} style={{marginRight:4,marginBottom:-1}} />Your data is used solely for hiring evaluation.</p>
      </form>
    </div>
  );
}
